import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorInjectionAPI } from '@atlaskit/editor-common/types';

import { expValEquals } from '@confluence/feature-experiments';

/**
 * Hook that intercepts the space key shortcut in the editor when
 * the cwr_blank_object_experiment is variant1 or variant2.
 *
 * Normally, pressing space in an empty editor opens inline Rovo.
 * When this hook is active, it captures the space keydown event in
 * the capture phase (before ProseMirror handles it) and instead
 * invokes the provided onSpaceKeyPressed callback (variant-specific
 * behaviour lives in the caller).
 *
 * This hook is intentionally implemented as a DOM capture-phase listener
 * rather than modifying the editor plugin, so that all experiment-specific
 * code stays in the Confluence layer and can be easily removed if the
 * experiment doesn't go to production. If it does go to production, we will work
 * with editor team to determine space key functionality long-term.
 */

export function useSpaceKeyShortcutOverride({
	isActive,
	isTypeAheadOpen,
	isRovoLLMEnabled,
	onSpaceKeyPressed,
	isLivePage,
	editorAPI,
}: {
	/** Whether the shortcut override should be active (e.g. blank page quick actions are showing, prompt not yet submitted) */
	isActive: boolean;
	/** Whether the typeahead/slash menu is currently open */
	isTypeAheadOpen: boolean;
	/** Whether Rovo LLM is enabled for the current user */
	isRovoLLMEnabled: boolean;
	/** Callback to invoke when the space key is pressed (e.g. show the CWR experience) */
	onSpaceKeyPressed: () => void;
	/** Whether the current page is a live doc */
	isLivePage: boolean;
	editorAPI?: EditorInjectionAPI<any, any> | null;
}): void {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onSpaceKeyPressedRef = useRef(onSpaceKeyPressed);
	onSpaceKeyPressedRef.current = onSpaceKeyPressed;

	// Store editorAPI in a ref so the listener always reads the latest
	// reference without having to be torn down and re-registered every time
	// the editorAPI identity changes.
	const editorAPIRef = useRef(editorAPI);
	editorAPIRef.current = editorAPI;

	useEffect(() => {
		if (
			!isActive ||
			isTypeAheadOpen ||
			!isRovoLLMEnabled ||
			(!expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') &&
				!expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2'))
		) {
			return;
		}

		const handleSpaceKeyCapture = (e: KeyboardEvent) => {
			// Only intercept the bare space key (no modifiers)
			if (e.key !== ' ' || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
				return;
			}

			const target = e.target as HTMLElement | null;
			if (!target?.closest?.('.ProseMirror')) {
				return;
			}

			// When we have access to the editor's focus shared state, only
			// intercept if the FullPageEditor itself currently has focus. This
			// prevents the override from firing for other ProseMirror editors
			// on the page (e.g. the Rovo chat sidebar input) which also match
			// the `.ProseMirror` selector above.
			const currentEditorAPI = editorAPIRef.current;
			const focusSharedState = currentEditorAPI?.focus?.sharedState?.currentState?.();
			if (currentEditorAPI && !focusSharedState?.hasFocus) {
				return;
			}

			// Prevent ProseMirror from handling the space key (which would open inline Rovo)
			e.preventDefault();
			e.stopPropagation();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'BlankObjectExperienceButtons',
					action: 'used',
					actionSubject: 'shortcut',
					actionSubjectId: 'createWithRovoBlankObject',
					attributes: {
						variant: expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1')
							? 'variant1'
							: 'variant2',
						contentType: isLivePage ? 'liveDoc' : 'page',
						buttonType: 'cwr',
					},
				},
			}).fire();

			// Delegate to the caller. In variant1 this shows the CWR experience;
			// in variant2 it focuses the already-rendered CreateWithRovoExperience input.
			onSpaceKeyPressedRef.current();
		};

		// Use capture phase to intercept before ProseMirror's keymap handler
		document.addEventListener('keydown', handleSpaceKeyCapture, true);
		return () => {
			document.removeEventListener('keydown', handleSpaceKeyCapture, true);
		};
	}, [isActive, isTypeAheadOpen, isRovoLLMEnabled, createAnalyticsEvent, isLivePage]);
}
